import { LiveAnnouncer } from '@angular/cdk/a11y';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  inject,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { CasePipe, CaseType } from '../../../pipes/case.pipe';
import {
  buildSearchStatusMessage,
  SearchFilterStatusComponent,
} from '../labels/search-filter-status.component';
import { BaseControlComponent } from './base-control.component';
import { InputComponent } from './input.component';

@Component({
  selector: 'app-filter-input',
  imports: [
    ReactiveFormsModule,
    CommonModule,
    InputTextModule,
    InputGroupAddonModule,
    InputComponent,
    CasePipe,
    SearchFilterStatusComponent,
  ],
  template: `
    <div
      class="tw-flex"
      [class.tw-flex-col]="vertical"
      [class.tw-items-center]="!vertical"
      [class.tw-pointer-events-none]="disabled"
      [class.tw-opacity-50]="disabled"
    >
      <app-search-filter-status
        announceMode="filter"
        [count]="statusCount"
        [query]="statusQuery"
        [statusRevision]="statusRevision"
        [enabled]="!asyncSearch"
        statusStyle="matching"
        [recordLabel]="recordLabel"
        [recordLabelPlural]="recordLabelPlural"
      />
      @if (asyncSearch) {
        <app-search-filter-status
          announceMode="message"
          [message]="searchPending ? searchPendingMessage : ''"
        />
      }
      <div [class]="classes">
        <app-input
          [control]="control"
          [placeholder]="label | case: 'sentence'"
          [showHelpText]="false"
          icon="fa fa-search fa-light"
          [large]="true"
          inputMode="search"
          (clear)="control.setValue(null)"
          [showAudio]="true"
        ></app-input>
      </div>

      <div
        [class.tw-pl-4]="!vertical"
        [class.tw-pl-2]="vertical"
        [class.tw-pt-4]="vertical"
        class="tw-font-bold tw-text-[var(--sed-black-80-color)]"
        aria-hidden="true"
      >
        <span [ngPlural]="recordCount">
          <ng-template ngPluralCase="=0">
            No {{ recordLabelPlural }}
          </ng-template>
          <ng-template ngPluralCase="=1">
            1 {{ recordLabel | case: recordLabelCase }}
          </ng-template>
          <ng-template ngPluralCase="other">
            {{ recordCount }}
            {{ recordLabelPlural | case: recordLabelCase }}
          </ng-template>
        </span>
        @if (control.value) {
          <span> matching '{{ control.value }}' </span>
        }
      </div>
    </div>
  `,
  styles: ``,
})
export class FilterInputComponent
  extends BaseControlComponent
  implements OnInit, OnChanges
{
  private liveAnnouncer = inject(LiveAnnouncer);
  private cdr = inject(ChangeDetectorRef);

  private asyncAnnounceGeneration = 0;

  @Input()
  recordLabel = 'record';

  @Input()
  recordLabelPlural: string;

  @Input()
  recordLabelCase: CaseType = 'lower';

  @Input()
  recordCount: number;

  @Input()
  classes: string;

  @Input()
  vertical = false;

  /**
   * When true, parent search is API-backed (debounced). Do not announce on
   * keystrokes — call syncSearchFilterStatus or syncSearchFilterError after load.
   */
  @Input()
  asyncSearch = false;

  /** True while parent search is in flight; cancels in-flight async announces. */
  @Input()
  searchPending = false;

  /** Announced when {@link searchPending} is true (async API search). */
  @Input()
  searchPendingMessage = 'Loading new content';

  /** Count/query passed to the live region (sync vs async paths). */
  protected statusCount = 0;
  protected statusQuery: string | null = '';
  protected statusRevision = 0;

  /** After async API search completes, pass count/query from the response. */
  syncSearchFilterStatus(count: number, query: string): void {
    const trimmedQuery = query.trim();
    this.statusCount = count;
    this.statusQuery = trimmedQuery;
    this.statusRevision++;

    if (this.asyncSearch) {
      this.announceAsyncFilterStatus(count, trimmedQuery);
    }

    this.cdr.markForCheck();
  }

  /** After async API search fails; table shows error UI via [error]. */
  syncSearchFilterError(message = 'Search failed. Please try again.'): void {
    if (this.asyncSearch) {
      this.announceAsyncMessage(message);
    }
  }

  ngOnInit(): void {
    if (!this.label) {
      this.label = 'Search';
    }

    this.recordLabelPlural ??= this.recordLabel + 's';

    if (!this.asyncSearch) {
      this.dispose.subs = this.control.valueChanges.subscribe(() =>
        this.syncFromBindings(),
      );
      this.syncFromBindings();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.asyncSearch) {
      if (changes['searchPending']?.currentValue === true) {
        this.asyncAnnounceGeneration++;
      }
      return;
    }
    if (changes['recordCount'] || changes['searchPending']) {
      this.syncFromBindings();
    }
  }

  private announceAsyncFilterStatus(count: number, query: string): void {
    this.announceAsyncMessage(
      buildSearchStatusMessage({
        count,
        query,
        style: 'matching',
        recordLabel: this.recordLabel,
        recordLabelPlural: this.recordLabelPlural,
      }),
    );
  }

  private announceAsyncMessage(message: string): void {
    this.asyncAnnounceGeneration++;
    const gen = this.asyncAnnounceGeneration;

    if (!message) {
      this.liveAnnouncer.clear();
      return;
    }

    this.liveAnnouncer.clear();
    queueMicrotask(() => {
      if (gen !== this.asyncAnnounceGeneration) {
        return;
      }
      this.liveAnnouncer.announce(message, 'polite');
    });
  }

  private syncFromBindings(): void {
    this.statusCount = this.recordCount ?? 0;
    this.statusQuery = this.control.value;
  }
}
